import DOMPurify from "dompurify";
import { marked } from "marked";
import katex from "katex";
import { useEffect, useRef } from "react";

interface Props {
  content: string;
}

/**
 * Renders markdown content with KaTeX math support.
 * Uses DOMPurify to sanitize HTML before rendering - this is safe against XSS.
 */
export function MarkdownRenderer({ content }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const renderContent = async () => {
      // Process LaTeX math expressions
      const processedContent = content
        .replace(/\$\$([^\$]+)\$\$/g, (_, formula) =>
          katex.renderToString(formula, {
            displayMode: true,
            throwOnError: false,
            fleqn: true,
          })
        )
        .replace(/\$([^\$]+)\$/g, (_, formula) =>
          katex.renderToString(formula, {
            displayMode: false,
            throwOnError: false,
          })
        );

      // Convert markdown to HTML
      const html = marked(processedContent, { async: false }) as string;

      // Sanitize HTML with DOMPurify to prevent XSS attacks
      const sanitizedHtml = DOMPurify.sanitize(html);

      // Safe to use innerHTML after DOMPurify sanitization
      if (containerRef.current) {
        containerRef.current.innerHTML = sanitizedHtml;
      }
    };

    renderContent();
  }, [content]);

  return <div ref={containerRef} className="prose max-w-full w-full break-words" />;
}
